import { reactive } from 'vue';

// Global tooltip state - only one tooltip can be active at a time
const globalTooltipState = reactive({
  activeTooltipId: null as string | null,
  registry: new Map<string, () => void>(), // Map of tooltip IDs to their hide functions
});

export function useGlobalTooltip() {
  // Register a tooltip with the global system
  function registerTooltip(id: string, hideFunction: () => void) {
    globalTooltipState.registry.set(id, hideFunction);
  }

  // Unregister a tooltip when component unmounts
  function unregisterTooltip(id: string) {
    globalTooltipState.registry.delete(id);
    if (globalTooltipState.activeTooltipId === id) {
      globalTooltipState.activeTooltipId = null;
    }
  }

  // Show a tooltip and hide all others
  function showTooltip(id: string) {
    // If this tooltip is already active, do nothing
    if (globalTooltipState.activeTooltipId === id) {
      return;
    }

    // Hide the currently active tooltip if any
    if (globalTooltipState.activeTooltipId) {
      const hideFunction = globalTooltipState.registry.get(globalTooltipState.activeTooltipId);
      if (hideFunction) {
        hideFunction();
      }
    }

    // Set this tooltip as active
    globalTooltipState.activeTooltipId = id;
  }

  // Hide a specific tooltip
  function hideTooltip(id: string) {
    if (globalTooltipState.activeTooltipId === id) {
      globalTooltipState.activeTooltipId = null;
    }
  }

  // Hide all tooltips
  function hideAllTooltips() {
    if (globalTooltipState.activeTooltipId) {
      const hideFunction = globalTooltipState.registry.get(globalTooltipState.activeTooltipId);
      if (hideFunction) {
        hideFunction();
      }
      globalTooltipState.activeTooltipId = null;
    }
  }

  return {
    registerTooltip,
    unregisterTooltip,
    showTooltip,
    hideTooltip,
    hideAllTooltips,
    activeTooltipId: globalTooltipState.activeTooltipId,
  };
}